import React, { useEffect, useState } from "react";
import { Navigate, useRoutes, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { wrapUseRoutes } from "@sentry/react";
import { AuthLayout } from "./components/AuthLayout";
import { userPermissionsSelector, validateToken } from "./store/slices/userSlice";
import { AUTH_REQUEST_KEYS, authV2Selector, checkAuthV2Session } from "./store/slices/authV2Slice";
import AuthWrapper from "./containers/AuthWrapper";
import Application from "./components/Application";
import ApplicationsList from "./components/Application/ApplicationsList";
import NotFound from "./pages/NotFound";
import InviteUsers from "./components/Register/InviteUsers";
import Settings from "./components/Settings";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import UserList from "./components/User/UserList";
import Billing from "./components/Billing";
import QuotesList from "./components/Quote/QuoteList";
import Quote from "./components/Quote";
import ContactDetails from "./pages/ContactDetails";
import CustomerDocumentsUpload from "./pages/CustomerDocumentsUpload";
import AssetDocumentsUpload from "./pages/AssetDocumentsUpload";
import Calculator from "./components/Calculator";
import CalculatorList from "./components/Calculator/CalculatorList";
import OrganisationList from "./components/Organisation/OrganisationList";
import OrganisationAdd from "./components/Organisation/OrganisationAdd";
import RolePermission from "./components/Settings/RolePermission";
import { Permission } from "./components/Settings/RolePermission/Permission";
import { PermissionGate } from "./components/PermissionGate";

// Unauthenticated pages
import { AuthSignIn } from "./pages/AuthSignIn";
import { AuthMagic } from "./pages/AuthMagic";
import { AuthMfa } from "./pages/AuthMfa";
import { AuthMfaEnrolment } from "./pages/AuthMfaEnrolment";
import { AuthVerifyEmail } from "./pages/AuthVerifyEmail";
import { AuthResetPassword } from "./pages/AuthResetPassword";
import { AuthSelectOrganisation } from "./pages/AuthSelectOrganisation";
import {
  AuthInviteLayout,
  AuthInviteSetName,
  AuthInviteSetPassword,
  AuthInviteSetMfa,
  AuthInviteAccept,
} from "./pages/AuthInvite";
import TemplateEditorWrapper from "./components/Settings/Templates/TemplateEditorWrapper";
import Disclosure from "./pages/Disclosure";
import { Status } from "./pages/Status";
import Reporting from "./components/Reporting";
import { publicRoutes, ROUTES } from "./constants";
import { Loader } from "./components/Loader";
import AdminAuthWrapper from "./containers/AdminAuthWrapper";
import TenantList from "./components/Admin/Tenant/TenantList";
import TenantDetail from "./components/Admin/Tenant";
import ServiceList from "./components/Admin/Service/ServiceList";
import InvoiceList from "./components/Admin/Invoice/InvoiceList";
import InvoiceDetails from "./components/Admin/Invoice";

const useSentryRoutes = wrapUseRoutes(useRoutes);

export default function Router(props) {
  const dispatch = useDispatch();
  const location = useLocation();
  const userPermission = useSelector(userPermissionsSelector);
  const { session, loading: authV2Loading } = useSelector(authV2Selector);
  // `checked` means the backend session check has completed; `authenticated` is its result.
  // Until `checked` is true, an unauthenticated value still represents an unknown session.
  const [isAuthenticated, setAuthenticated] = useState(false);
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
  // Prevent a remount or route change from starting another check while one is in flight.
  const isCheckingSession = authV2Loading[AUTH_REQUEST_KEYS.checkSession];

  useEffect(() => {
    let isMounted = true;
    const isPublicPath = publicRoutes.some((routePath) => location.pathname.startsWith(routePath));

    if (isPublicPath || hasCheckedAuth || isCheckingSession) {
      return () => {
        isMounted = false;
      };
    }

    const runAuthCheck = async () => {
      // Reuse a completed Redux session check; otherwise ask the backend for the current session.
      let authenticated = session.authenticated;

      if (!session.checked) {
        const sessionResult = await dispatch(checkAuthV2Session());
        authenticated = Boolean(sessionResult?.payload?.authenticated);
      }

      if (!isMounted) {
        return;
      }

      if (authenticated) {
        setAuthenticated(true);
        // Keep existing user permission hydration for app routes.
        // @ts-expect-error: existing untyped dispatch pattern
        await dispatch(validateToken());
      } else {
        setAuthenticated(false);
      }

      setHasCheckedAuth(true);
      return;
    };

    runAuthCheck();

    return () => {
      isMounted = false;
    };
  }, [dispatch, hasCheckedAuth, isCheckingSession, location.pathname, session.authenticated, session.checked]);

  return useSentryRoutes([
    { path: ROUTES.CUSTOMER_UPLOAD_DOCUMENTS, element: <CustomerDocumentsUpload /> },
    { path: ROUTES.ASSET_UPLOAD_DOCUMENTS, element: <AssetDocumentsUpload /> },
    { path: ROUTES.CONTACT_DETAILS, element: <ContactDetails /> },
    { path: ROUTES.PRIVACY, element: <Privacy /> },
    { path: ROUTES.DISCLOSURE, element: <Disclosure /> },
    {
      path: ROUTES.SIGN_IN,
      element: <Navigate to={ROUTES.AUTH.SIGN_IN_FULL} replace />,
    },
    {
      path: ROUTES.AUTH.ROOT,
      element: <AuthLayout />,
      children: [
        { index: true, element: <Navigate to={ROUTES.AUTH.SIGN_IN_FULL} replace /> },
        { path: ROUTES.AUTH.SIGN_IN, element: <AuthSignIn /> },
        { path: ROUTES.AUTH.MAGIC, element: <AuthMagic /> },
        { path: ROUTES.AUTH.MFA, element: <AuthMfa /> },
        { path: ROUTES.AUTH.MFA_ENROLMENT, element: <AuthMfaEnrolment /> },
        { path: ROUTES.AUTH.VERIFY_EMAIL, element: <AuthVerifyEmail /> },
        { path: ROUTES.AUTH.RESET_PASSWORD, element: <AuthResetPassword /> },
        { path: ROUTES.AUTH.SELECT_ORGANISATION, element: <AuthSelectOrganisation /> },
        {
          path: ROUTES.AUTH.INVITE.ROOT,
          element: <AuthInviteLayout />,
          children: [
            { path: ROUTES.AUTH.INVITE.SET_NAME, element: <AuthInviteSetName /> },
            { path: ROUTES.AUTH.INVITE.SET_PASSWORD, element: <AuthInviteSetPassword /> },
            { path: ROUTES.AUTH.INVITE.SET_MFA, element: <AuthInviteSetMfa /> },
            { path: ROUTES.AUTH.INVITE.ACCEPT, element: <AuthInviteAccept /> },
          ],
        },
      ],
    },
    { path: ROUTES.STATUS, element: <Status /> },
    {
      path: ROUTES.ROOT,
      element: !hasCheckedAuth ? (
        <Loader />
      ) : isAuthenticated ? (
        <AuthWrapper />
      ) : (
        <Navigate to={ROUTES.AUTH.SIGN_IN_FULL} replace />
      ),
      children: [
        { path: ROUTES.APP.CONTACT_DETAILS, element: <ContactDetails /> },
        { path: ROUTES.APP.NOT_FOUND, element: <NotFound /> },
        { path: ROUTES.APP.TERMS_AND_CONDITIONS, element: <Terms /> },
        { path: ROUTES.APP.INVITE_USERS, element: <InviteUsers /> },
        { path: ROUTES.APP.QUOTES, element: <QuotesList /> },
        { path: ROUTES.APP.QUOTE_DETAIL, element: <Quote /> },
        { path: ROUTES.ROOT, element: <Navigate to={ROUTES.APPLICATIONS_FULL} /> },
        { path: ROUTES.APP.INVITE_USER, element: <InviteUsers /> },
        {
          path: ROUTES.APP.APPLICATIONS,
          index: true,
          element: (
            <PermissionGate>
              <ApplicationsList />
            </PermissionGate>
          ),
        },
        { path: ROUTES.APP.APPLICATION_DETAIL, element: <Application /> },
        { path: ROUTES.APP.ORGANISATION, element: <OrganisationList /> },
        {
          path: ROUTES.APP.ORGANISATION_NEW,
          element: userPermission?.org?.create ? <OrganisationAdd /> : <Navigate to={ROUTES.APP.ORGANISATION} />,
        }, // route protected needs
        {
          path: ROUTES.APP.ORGANISATION_DETAIL,
          element: userPermission?.org?.edit ? <OrganisationAdd /> : <Navigate to={ROUTES.APP.ORGANISATION} />,
        },
        {
          path: ROUTES.APP.ORGANISATION_ROLE_PERMISSIONS,
          element: userPermission?.org?.permission_edit ? (
            <RolePermission />
          ) : (
            <Navigate to={ROUTES.APP.ORGANISATION} />
          ),
        }, // route protected needs
        { path: ROUTES.APP.USERS, element: <UserList /> },
        { path: ROUTES.APP.CALCULATOR, element: <CalculatorList /> },
        { path: ROUTES.APP.CALCULATOR_DETAIL, element: <Calculator /> },
        {
          path: ROUTES.APP.USER_ROLE_PERMISSIONS,
          element: userPermission?.user?.permission_edit ? <Permission /> : <Navigate to={ROUTES.APP.USERS} />,
        }, // route protected needs
        {
          path: ROUTES.APP.SETTINGS,
          children: [
            { path: ROUTES.APP.SETTINGS_TEMPLATE, element: <TemplateEditorWrapper /> },
            { path: ROUTES.APP.SETTINGS_PATH, element: <Settings /> },
          ],
        },
        { path: ROUTES.APP.BILLING_PATH, element: true ? <Billing /> : <Navigate to={ROUTES.ROOT} /> },
        { path: ROUTES.APP.REPORTING, element: true ? <Reporting /> : <Navigate to={ROUTES.ROOT} /> },
        {
          path: ROUTES.ADMIN.ROOT,
          element: <AdminAuthWrapper />,
          children: [
            { path: ROUTES.ADMIN.TENANTS, element: <TenantList /> },
            { path: ROUTES.ADMIN.TENANT_DETAIL, element: <TenantDetail /> },
            { path: ROUTES.ADMIN.SERVICES, element: <ServiceList /> },
            { path: ROUTES.ADMIN.SERVICE_DETAIL, element: <div>Service detail</div> },
            { path: ROUTES.ADMIN.INVOICES, element: <InvoiceList /> },
            { path: ROUTES.ADMIN.INVOICE_DETAIL, element: <InvoiceDetails /> },
          ],
        },
        { path: ROUTES.APP.WILDCARD, element: <Navigate to={ROUTES.NOT_FOUND} replace /> },
      ],
    },
  ]);
}
